import React, { useEffect, useState } from "react";
import "./index.css";
import { Box, TextField, Tooltip, Typography } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Swal from "sweetalert2";
import {
  ArrowLeftIcon,
  CheckIcon,
  PhotoIcon,
  PrinterIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import {
  createVendorId,
  getPrinterTemplates,
  updatePrinterTemplate,
} from "../../../../../../config/Services";
import { PosCustomButton } from "../../../../../../customComponents/PosCustomButton/PosCustomButton";
import { setInLocalStorage } from "../../../../../../utils/localStorageHelpers";
import { LABEL_SIZES } from "../../../../delivery/components/LabelPrintModal/LabelPrintModal";
import useAuthModal from "../../../hooks/useAuthModal";
import usePrint from "../../../hooks/usePrint";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import Navbar from "../../../../posnavbar/Navbar";
import template00601Preview from "../../../../../../assets/images/template_006_01_preview.svg";

const MUI_INPUT_STYLES = {
  "& .MuiInputBase-input": { color: "black !important" },
  "& .MuiInputLabel-root": { color: "black !important" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "black !important" },
    "&:hover fieldset": { borderColor: "#ccc !important" },
  },
};

const DEFAULT_LABEL_SIZE = LABEL_SIZES.find((size) => size.value === "100x50") || LABEL_SIZES[0];
const PREV_MAX_W = 280;
const PREV_MAX_H = 160;

// Frontend-only template, not returned by getPrinterTemplates. Same as template_002 but prints the
// variant name instead of the product name — only offered to non-restaurant (grocery) stores.
const VARIANT_NAME_TEMPLATE = { id: "template_002_01", name: "Variant Name (002_01)", url: "" };

// Frontend-only template, not returned by getPrinterTemplates. Customised copy of template_006
// (full item names, Order Id, B2C tweaks) — only offered to restaurant stores.
const RESTAURANT_006_01_TEMPLATE = {
  id: "template_006_01",
  name: "Template 006_01",
  url: template00601Preview,
};

export const PrinterConfigurationSettings = () => {
  const [activeTab, setActiveTab] = useState("thermal");
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState("");
  const [charsPerLine, setCharsPerLine] = useState("42");
  const [selectedLabelSize, setSelectedLabelSize] = useState(DEFAULT_LABEL_SIZE);
  const [labelsPerRow, setLabelsPerRow] = useState(1);
  const [customLine1, setCustomLine1] = useState("");
  const [customLine2, setCustomLine2] = useState("");
  const [isCustomPrinting, setIsCustomPrinting] = useState(false);
  const [isTestLabelPrinting, setIsTestLabelPrinting] = useState(false);

  const showSnackbar = useSnackbar();
  const { printUSB, printItemLabels } = usePrint();
  const isLabelPrinterEnabled =
    localStorage.getItem("retailType") !== "GROCERY" &&
    JSON.parse(localStorage.getItem("appConfig") || "{}")?.all?.LABEL_PRINTER?.flag === true;
  const { renderAuthModal, openAuthModal } = useAuthModal();

  const userDetails = localStorage.getItem("retailUserDetails");
  const orgId = localStorage.getItem("orgId");
  const parsedUserDetails = userDetails ? JSON.parse(userDetails) : null;
  const uidx = parsedUserDetails?.uidx || "";
  const userName = parsedUserDetails
    ? `${parsedUserDetails?.firstName || ""} ${parsedUserDetails?.secondName || ""}`.trim()
    : "NA";

  const previewTotalWidthMm =
    selectedLabelSize.widthMm * labelsPerRow + selectedLabelSize.gapMm * (labelsPerRow - 1);
  const prevScale = Math.min(
    PREV_MAX_W / previewTotalWidthMm,
    PREV_MAX_H / selectedLabelSize.heightMm
  );
  const prevW = Math.round(previewTotalWidthMm * prevScale);
  const prevH = Math.round(selectedLabelSize.heightMm * prevScale);
  const singleColPrevW = Math.round(selectedLabelSize.widthMm * prevScale);

  const newSwal = Swal.mixin({
    customClass: {
      confirmButton: "button button-success",
      cancelButton: "button button-error",
      container: "close-session-swal-container",
    },
    buttonsStyling: false,
  });

  const selectedTemplate = templates.find((tpl) => tpl?.id === template);
  const selectedImage = selectedTemplate?.url;

  useEffect(() => {
    getPrinterTemplates()
      .then((res) => {
        if (res?.data?.es == 0) {
          const templatesList =
            res?.data?.templateListDtoList?.map((item) => ({
              id: item?.templateId,
              name: item?.templateName,
              url: item?.url,
            })) || [];
          if (
            localStorage.getItem("retailType") !== "RESTAURANT" &&
            !templatesList.some((tpl) => tpl?.id === VARIANT_NAME_TEMPLATE.id)
          ) {
            // Same layout as template_002, so reuse its preview image
            const baseTemplateUrl =
              templatesList.find((tpl) => tpl?.id === "template_002")?.url || "";
            templatesList.push({ ...VARIANT_NAME_TEMPLATE, url: baseTemplateUrl });
          }
          if (
            localStorage.getItem("retailType") === "RESTAURANT" &&
            !templatesList.some((tpl) => tpl?.id === RESTAURANT_006_01_TEMPLATE.id)
          ) {
            templatesList.push(RESTAURANT_006_01_TEMPLATE);
          }
          setTemplates(templatesList);
          setTemplate(templatesList[0]?.id || "");
        }
      })
      .catch(() => {
        showSnackbar("error");
      });
  }, []);

  const handleSave = () => {
    const payload = {
      updatedBy: uidx,
      updatedByName: userName,
      retailId: orgId,
      templateId: template,
      charactersPerLine: Number(charsPerLine),
    };

    newSwal
      .fire({
        title: "Are you sure you want to change the settings?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Proceed",
        cancelButtonText: "Cancel",
        backdrop: "rgba(0,0,0,0.4)",
      })
      .then((result) => {
        if (result.isConfirmed) {
          openAuthModal(() => {
            updatePrinterTemplate(payload)
              .then((res) => {
                setInLocalStorage("orgConfig", res?.data?.configuration);
                newSwal.fire({
                  title: "Changed!",
                  text: "Settings have been successfully updated.",
                  icon: "success",
                  timer: 1500,
                  showConfirmButton: false,
                });
              })
              .catch(() => {
                newSwal.fire({
                  title: "Error",
                  text: "Failed to update settings. Please try again.",
                  icon: "error",
                });
              });
          });
        }
      });
  };

  const getVendorID = async () => {
    const device = await navigator.usb.requestDevice({ filters: [] });
    const payload = {
      vendorId: `${device.vendorId}`,
      peripheralType: "THERMAL",
      createdBy: uidx,
    };

    createVendorId(payload)
      .then((res) => {
        if (res?.data?.statusCode == 10093) {
          newSwal.fire({
            title: "Error",
            text: "Printer is already set-up in our app",
            icon: "error",
          });
        } else if (res?.data?.statusCode == 200) {
          newSwal.fire({
            title: "Set-up",
            text: "Printer has been successfully set-up.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      })
      .catch(() => {
        newSwal.fire({
          title: "Error",
          text: "Failed to set-up the printer. Please try again.",
          icon: "error",
        });
      });
  };

  const handleGenerateVendorId = () => {
    Swal.fire({
      title: "Connect Your Printer",
      text: "Please keep your printer connected to the usb port to continue with the configuration. Make sure it's powered on and no other devices are connected.",
      icon: "info",
      confirmButtonText: "Got it",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Select your printer",
          text: "In the next popup, please select your printer from the list of devices",
          icon: "info",
          confirmButtonText: "Got it",
        }).then((nextResult) => {
          if (nextResult.isConfirmed) {
            getVendorID();
          }
        });
      }
    });
  };

  const handleComingSoon = () => {
    newSwal.fire({
      title: "Coming soon",
      text: "This feature is coming soon.",
      icon: "info",
      confirmButtonText: "Okay",
    });
  };

  const handleTestLabelPrint = async () => {
    setIsTestLabelPrinting(true);
    try {
      await printItemLabels({
        items: [
          {
            productName: "Test Product",
            variantName: "Regular",
            quantity: 1,
            addonProduct: [{ itemName: "Extra Sauce" }],
          },
        ],
        customerName: "Walk-in",
        token: "TEST-1",
        labelWidthMm: selectedLabelSize.widthMm,
        labelHeightMm: selectedLabelSize.heightMm,
        gapMm: selectedLabelSize.gapMm,
        labelsPerRow,
      });
    } catch {
      // printer errors are handled inside usePrint
    } finally {
      setIsTestLabelPrinting(false);
    }
  };

  const handleCustomLabelPrint = async () => {
    if (!customLine1.trim() && !customLine2.trim()) return;
    setIsCustomPrinting(true);
    try {
      await printItemLabels({
        items: [
          {
            productName: customLine1.trim() || " ",
            variantName: customLine2.trim() || undefined,
            addonProduct: [],
          },
        ],
        customerName: "",
        token: "",
        labelWidthMm: selectedLabelSize.widthMm,
        labelHeightMm: selectedLabelSize.heightMm,
        gapMm: selectedLabelSize.gapMm,
        labelsPerRow,
      });
    } catch {
      // printer errors are handled inside usePrint
    } finally {
      setIsCustomPrinting(false);
    }
  };

  return (
    <Navbar>
      <Box className="printer-config-page-shell">
        {renderAuthModal()}

        <Box className="printer-config-workspace">
          <section className="thermal-template-page-bar">
            <div className="thermal-template-page-bar__title-wrap">
              <button
                type="button"
                className="thermal-template-page-bar__back"
                onClick={() => window.history.back()}
                aria-label="Back"
              >
                <ArrowLeftIcon />
              </button>
              <h1 className="thermal-template-page-bar__title">Choose a bill template</h1>
            </div>

            {activeTab === "thermal" && (
              <button
                type="button"
                className="thermal-template-page-bar__save"
                onClick={handleSave}
              >
                <SaveOutlinedIcon />
                <span className="thermal-template-page-bar__save-full">Save Template</span>
                <span className="thermal-template-page-bar__save-short">Save</span>
              </button>
            )}
          </section>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <p className="printer-config-title-p">Printer Configuration</p>

            <button
              type="button"
              className="printer-config-setup-btn"
              onClick={handleGenerateVendorId}
            >
              <span>Initial Print Setup</span>
              <PrinterIcon />
            </button>
          </Box>

          <section className="pc-tab-row">
            <button
              type="button"
              className={`pc-tab-btn ${activeTab === "thermal" ? "pc-tab-btn--active" : ""}`}
              onClick={() => setActiveTab("thermal")}
            >
              Thermal Printer
            </button>
            <button
              type="button"
              className={`pc-tab-btn ${activeTab === "label" ? "pc-tab-btn--active" : ""}`}
              onClick={() => setActiveTab("label")}
            >
              Label Printer Tester
            </button>
          </section>

          <Box className="printer-config-content-row">
            <Box className="printer-config-content-main">
              {activeTab === "thermal" ? (
                <>
                  <section className="printer-configuration-input-wrapper">
                    <Box className="thermal-template-library">
                      <Box className="thermal-template-library__header">
                        <Box className="thermal-template-library__heading">
                          <Typography className="thermal-template-library__eyebrow">
                            Template Library
                          </Typography>
                          <Typography className="thermal-template-library__title">
                            Several ready-made bill styles
                          </Typography>
                          <Typography className="thermal-template-library__copy">
                            Compare options in a cleaner grid, then fine-tune the receipt header and footer before saving.
                          </Typography>
                        </Box>
                        <Typography className="thermal-template-library__count">
                          {templates?.length} templates
                        </Typography>
                      </Box>

                      {templates?.length > 0 ? (
                        <Box className="thermal-template-grid">
                          {templates?.map((tpl) => {
                            const isSelected = tpl?.id === template;

                            return (
                              <button
                                type="button"
                                key={tpl?.id}
                                className={`thermal-template-card ${
                                  isSelected ? "thermal-template-card--active" : ""
                                }`}
                                onClick={() => setTemplate(tpl?.id)}
                                aria-pressed={isSelected}
                              >
                                <span className="thermal-template-card__preview">
                                  {tpl?.url ? (
                                    <img src={tpl?.url} alt="" className="thermal-template-card__image" />
                                  ) : (
                                    <span className="thermal-template-card__fallback">
                                      <span />
                                      <span />
                                      <span />
                                    </span>
                                  )}
                                </span>
                                <span className="thermal-template-card__body">
                                  <span className="thermal-template-card__name">{tpl?.name}</span>
                                  <span className="thermal-template-card__meta">
                                    Thermal receipt
                                  </span>
                                </span>
                                <span className="thermal-template-card__check" aria-hidden="true">
                                  {isSelected && <CheckIcon />}
                                </span>
                              </button>
                            );
                          })}
                        </Box>
                      ) : (
                        <Box className="thermal-template-empty">
                          No printer templates available
                        </Box>
                      )}
                    </Box>

                    <Box my={4}>
                      <TextField
                        fullWidth
                        type="number"
                        value={charsPerLine}
                        onChange={(e) => setCharsPerLine(e.target.value)}
                        inputProps={{ min: 30, max: 80 }}
                        label="Characters per line"
                        variant="outlined"
                        sx={MUI_INPUT_STYLES}
                      />
                      <Tooltip
                        title={
                          <>
                            Recommended for 50mm printers (32-40 chars)
                            <br />
                            Recommended for 80mm printers (42-48 chars)
                            <br />
                            Recommended for 110mm printers (72+ chars)
                          </>
                        }
                        placement="bottom"
                      >
                        <Typography variant="body2" color="primary" mt={1}>
                          Hover here for recommendation
                        </Typography>
                      </Tooltip>
                    </Box>
                  </section>

                  <section className="print-config-test-save-print-container print-config-template-action-row" style={{ justifyContent: "space-between" }}>
                    <Box className="print-config-selected-template" sx={{ minWidth: 0 }}>
                      <Typography className="print-config-selected-template__label">
                        Selected template
                      </Typography>
                      <Typography className="print-config-selected-template__name">
                        {selectedTemplate?.name || "Thermal receipt"}
                      </Typography>
                    </Box>
                    <button
                      type="button"
                      className="review-styles-btn"
                      style={{ height: "42px" }}
                      onClick={() => printUSB(false, true, template, Number(charsPerLine))}
                    >
                      Review Styles
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right arrow-icon" aria-hidden="true">
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                      </svg>
                    </button>
                  </section>

                  <section className="thermal-config-dummy-grid">
                    <button
                      type="button"
                      className="thermal-config-dummy-card"
                      onClick={handleComingSoon}
                    >
                      <div className="thermal-config-dummy-card__header">
                        <div className="thermal-config-dummy-card__icon">
                          <PhotoIcon />
                        </div>
                        <div>
                          <p className="thermal-config-dummy-card__eyebrow">Header Configuration</p>
                          <h2 className="thermal-config-dummy-card__title">Logo visibility</h2>
                          <p className="thermal-config-dummy-card__copy">
                            Keep the header simple and choose how the receipt title block should line up.
                          </p>
                        </div>
                      </div>

                      <div className="thermal-config-dummy-card__panel">
                        <div>
                          <p className="thermal-config-dummy-card__panel-title">Show logo</p>
                          <p className="thermal-config-dummy-card__panel-copy">
                            Display the brand mark in the receipt header.
                          </p>
                        </div>
                        <span className="thermal-config-dummy-card__switch" aria-hidden="true">
                          <span className="thermal-config-dummy-card__switch-thumb" />
                        </span>
                      </div>

                      <div className="thermal-config-dummy-card__segmented">
                        <span>Left</span>
                        <span>Center</span>
                        <span className="thermal-config-dummy-card__segmented-active">Right</span>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="thermal-config-dummy-card"
                      onClick={handleComingSoon}
                    >
                      <div className="thermal-config-dummy-card__header">
                        <div className="thermal-config-dummy-card__icon">
                          <SparklesIcon />
                        </div>
                        <div>
                          <p className="thermal-config-dummy-card__eyebrow">Footer Content</p>
                          <h2 className="thermal-config-dummy-card__title">Custom closing note</h2>
                          <p className="thermal-config-dummy-card__copy">
                            Add the message customers will see at the bottom of every printed bill.
                          </p>
                        </div>
                      </div>

                      <div className="thermal-config-dummy-card__note">
                        Thank you for dining with us. Visit again soon.
                      </div>
                    </button>
                  </section>
                </>
              ) : (
                <section className="label-tester-section">
                  <Box className="thermal-template-library">
                    <Box className="thermal-template-library__header">
                      <Box className="thermal-template-library__heading">
                        <Typography className="thermal-template-library__eyebrow">
                          Label Size
                        </Typography>
                        <Typography className="thermal-template-library__title">
                          Choose your label dimensions
                        </Typography>
                        <Typography className="thermal-template-library__copy">
                          Pick the paper size that matches your label rolls.
                        </Typography>
                      </Box>
                      <Typography className="thermal-template-library__count">
                        {LABEL_SIZES?.length} sizes
                      </Typography>
                    </Box>

                    <Box className="thermal-template-grid">
                      {LABEL_SIZES?.map((size) => {
                        const isSelected = selectedLabelSize?.value === size.value;
                        const maxDim = Math.max(size.widthMm, size.heightMm);
                        const scaledW = Math.round((size.widthMm / maxDim) * 60);
                        const scaledH = Math.round((size.heightMm / maxDim) * 60);

                        return (
                          <button
                            type="button"
                            key={size?.value}
                            className={`thermal-template-card ${
                              isSelected ? "thermal-template-card--active" : ""
                            }`}
                            onClick={() => setSelectedLabelSize(size)}
                            aria-pressed={isSelected}
                          >
                            <span className="thermal-template-card__preview">
                              <span
                                style={{
                                  display: "block",
                                  width: scaledW,
                                  height: scaledH,
                                  background: "#e2e8f0",
                                  border: "1.5px solid #cbd5e1",
                                  borderRadius: 3,
                                }}
                              />
                            </span>
                            <span className="thermal-template-card__body">
                              <span className="thermal-template-card__name">{size?.label}</span>
                              <span className="thermal-template-card__meta">
                                {size?.widthMm}mm × {size?.heightMm}mm
                              </span>
                            </span>
                            <span className="thermal-template-card__check" aria-hidden="true">
                              {isSelected && <CheckIcon />}
                            </span>
                          </button>
                        );
                      })}
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    type="number"
                    margin="normal"
                    label="Labels per row"
                    value={labelsPerRow}
                    onChange={(e) => setLabelsPerRow(Math.max(1, Number(e.target.value) || 1))}
                    inputProps={{ min: 1, max: 10 }}
                    sx={MUI_INPUT_STYLES}
                  />

                  <section className="print-config-test-save-print-container">
                    <PosCustomButton
                      title={isTestLabelPrinting ? "Printing..." : "Test Label Print"}
                      variant="blue-s"
                      width="100%"
                      height="42px"
                      disabled={isTestLabelPrinting}
                      onClickFunction={handleTestLabelPrint}
                    />
                  </section>

                  <Box className="label-custom-card">
                    <Typography className="label-preview-title">Custom Label</Typography>
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Line 1"
                      value={customLine1}
                      onChange={(e) => setCustomLine1(e.target.value)}
                      sx={MUI_INPUT_STYLES}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Line 2"
                      value={customLine2}
                      onChange={(e) => setCustomLine2(e.target.value)}
                      sx={MUI_INPUT_STYLES}
                    />
                    <PosCustomButton
                      title={isCustomPrinting ? "Printing..." : "Print Custom Label"}
                      variant="blue-s"
                      width="100%"
                      height="42px"
                      disabled={isCustomPrinting || (!customLine1.trim() && !customLine2.trim())}
                      onClickFunction={handleCustomLabelPrint}
                    />
                  </Box>
                </section>
              )}
            </Box>

            {activeTab === "thermal" && selectedImage && (
              <Box className="printer-config-content-side">
                <Box className="thermal-live-preview-card">
                  <Box className="thermal-live-preview-card__header">
                    <Box>
                      <Typography className="thermal-live-preview-card__eyebrow">
                        Live Preview
                      </Typography>
                      <Typography className="thermal-live-preview-card__title">
                        {selectedTemplate?.name || "Selected template"}
                      </Typography>
                    </Box>
                    <span className="thermal-live-preview-card__status">
                      <CheckIcon />
                      Active
                    </span>
                  </Box>

                  <Box className="thermal-live-preview-card__receipt-shell">
                    <Box className="thermal-live-preview-card__receipt-paper">
                      <img
                        src={selectedImage}
                        alt={`${selectedTemplate?.name || "Template"} preview`}
                        className="thermal-template-preview-image"
                      />
                    </Box>
                  </Box>

                  <Box className="thermal-live-preview-card__summary">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography className="thermal-live-preview-card__summary-title">
                        Selected template
                      </Typography>
                      <Typography className="thermal-live-preview-card__summary-text">
                        {selectedTemplate?.name || "Thermal receipt"}
                      </Typography>
                    </Box>
                    <button
                      type="button"
                      className="review-styles-btn"
                      style={{ height: "38px" }}
                      onClick={() => printUSB(false, true, template, Number(charsPerLine))}
                    >
                      Test Print
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right arrow-icon" aria-hidden="true">
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                      </svg>
                    </button>
                  </Box>
                </Box>
              </Box>
            )}

            {activeTab === "label" && (
              <Box className="printer-config-content-side" sx={{ flexDirection: "column", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", color: "#64748b", textTransform: "uppercase" }}>
                  Label Preview
                </Typography>

                <Box
                  sx={{
                    width: prevW,
                    height: prevH,
                    display: "flex",
                    flexDirection: "row",
                    gap: `${Math.round(selectedLabelSize.gapMm * prevScale)}px`,
                    border: "2px dashed #a0aec0",
                    borderRadius: "6px",
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "width 0.2s, height 0.2s",
                  }}
                >
                  {Array.from({ length: labelsPerRow }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: singleColPrevW,
                        height: "100%",
                        flexShrink: 0,
                        borderRight: i < labelsPerRow - 1 ? "1px dashed #cbd5e1" : "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "8px",
                        gap: "4px",
                      }}
                    >
                      <Typography sx={{ fontSize: Math.max(7, Math.min(10, prevH * 0.12)), color: "#374151", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                        Walk-in
                      </Typography>
                      <Typography sx={{ fontSize: Math.max(5, Math.min(8, prevH * 0.09)), color: "#9ca3af", letterSpacing: "0.05em" }}>
                        --------------------
                      </Typography>
                      <Typography sx={{ fontSize: Math.max(8, Math.min(13, prevH * 0.17)), fontWeight: 700, color: "#1a1a1a", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                        {customLine1.trim() || "Product Name"}
                      </Typography>
                      <Typography sx={{ fontSize: Math.max(7, Math.min(10, prevH * 0.12)), color: "#4a5568", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                        {customLine2.trim() || "Variant"}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Typography variant="body2" sx={{ fontSize: 12, color: "#64748b", textAlign: "center" }}>
                  {selectedLabelSize.widthMm}mm × {selectedLabelSize.heightMm}mm · {labelsPerRow} per row
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Navbar>
  );
};
